import { useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { DiaryContextType } from "../context/DiaryContext";
import { useDiaryContext } from "./useDiaryContext";
import {
  UrlRouteParams,
  useUrlNavigation,
  useUrlSearchParams
} from "./useUrlSearchParams";

const syncEditBookingFromPath = (
  pathParts: string[],
  editBookingId: string | undefined,
  setDiaryContext: (ctx: Partial<DiaryContextType>) => void
) => {
  const isEditRoute =
    pathParts.length >= 4 &&
    pathParts[1] === "booking" &&
    pathParts[3] === "edit";

  if (isEditRoute) {
    const bookingIdFromUrl = pathParts[2];
    if (bookingIdFromUrl && bookingIdFromUrl !== editBookingId) {
      setDiaryContext({ editBookingId: bookingIdFromUrl });
    }
    return;
  }

  if (editBookingId) {
    setDiaryContext({ editBookingId: undefined });
  }
};

// Syncs selectedBookingId from the URL path. Origin flags (openedFromEnquiry,
// openedFromSpotlight) are NOT managed here — they are set by the originating
// click handler (e.g. handleViewEnquiry) directly in DiaryContext, which is the
// single source of truth. This avoids reliance on location.state which is
// unreliable in the native WebView (WKWebView + loadHTMLString drops
// history.state — see CLAUDE.md lessons log).
const syncSelectedBookingFromPath = (
  pathParts: string[],
  selectedBookingId: string | undefined,
  setDiaryContext: (ctx: Partial<DiaryContextType>) => void
) => {
  const isBookingsRoute = pathParts.length >= 3 && pathParts[1] === "bookings";

  if (!isBookingsRoute) {
    if (selectedBookingId) {
      setDiaryContext({
        selectedBookingId: undefined,
        openedFromEnquiry: false,
        openedFromSpotlight: false
      });
    }
    return;
  }

  const bookingIdFromUrl = pathParts[2];

  if (bookingIdFromUrl && bookingIdFromUrl !== selectedBookingId) {
    setDiaryContext({ selectedBookingId: bookingIdFromUrl });
  }
};

const useUpdateStateFromURL = () => {
  const {
    date,
    setDiaryContext,
    selectedVenue,
    selectedView,
    editBookingId,
    selectedBookingId
  } = useDiaryContext();

  const { getToggleViewFromTab } = useUrlNavigation();
  const { venueId: urlVenueId, tab: urlTab } = useParams<UrlRouteParams>();
  const searchParams = useUrlSearchParams();
  const location = useLocation();

  const dateParam = searchParams.date;

  useEffect(() => {
    const pathParts = location.pathname.split("/");

    syncEditBookingFromPath(pathParts, editBookingId, setDiaryContext);
    syncSelectedBookingFromPath(pathParts, selectedBookingId, setDiaryContext);
  }, [location.pathname, editBookingId, selectedBookingId, setDiaryContext]);

  useEffect(() => {
    const updatedContext: Partial<DiaryContextType> = {};

    if (urlVenueId && urlVenueId !== selectedVenue) {
      updatedContext.selectedVenue = urlVenueId;
      updatedContext.isInitialising = true;
      updatedContext.bookings = undefined;
      updatedContext.bookingsFiltered = undefined;
      updatedContext.selectedBooking = undefined;
      updatedContext.selectedBookingV4 = undefined;
      updatedContext.venueConfig = {};
    }

    if (dateParam) {
      const currentDateString = date
        ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
        : null;
      if (dateParam !== currentDateString) {
        const [year, month, day] = dateParam.split("-").map(Number);
        const urlDate = new Date(year, month - 1, day);
        if (!isNaN(urlDate.getTime())) {
          updatedContext.date = urlDate;
        }
      }
    }

    if (urlTab) {
      const toggleView = getToggleViewFromTab(urlTab);
      if (toggleView !== selectedView) {
        updatedContext.selectedView = toggleView;
      }
    }

    if (Object.keys(updatedContext).length > 0) {
      setDiaryContext(updatedContext);
    }
  }, [urlVenueId, urlTab, dateParam, selectedVenue, selectedView, date]);
};

export default useUpdateStateFromURL;
